import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, chartA11yProps } from '@respectly/ui';
import type { UsageTrendPoint } from '@respectly/shared-types';

interface UsageTrendChartProps {
  data: UsageTrendPoint[];
  isLoading?: boolean;
}

const SERIES = [
  { key: 'campaigns', label: 'Campaigns', color: '#3b82f6' },
  { key: 'documents', label: 'Documents', color: '#8b5cf6' },
  { key: 'forms', label: 'Forms', color: '#10b981' },
  { key: 'voice', label: 'Voice', color: '#f59e0b' },
  { key: 'workflows', label: 'Workflows', color: '#a855f7' },
  { key: 'conversations', label: 'Messaging', color: '#06b6d4' },
] as const;

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const nonZero = payload.filter((p: any) => p.value > 0);
  if (!nonZero.length) return null;
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 shadow-lg text-xs min-w-[140px]">
      <p className="font-semibold text-slate-600 dark:text-slate-300 mb-2">{formatDate(label)}</p>
      {nonZero.map((p: any) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-3 mb-0.5">
          <div className="flex items-center gap-1.5">
            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-slate-500 dark:text-slate-400">{p.name}</span>
          </div>
          <span className="font-semibold text-slate-900 dark:text-slate-100">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

/** Per-series totals over the whole window, for the chart's accessible summary. */
function buildTrendLabel(data: UsageTrendPoint[]): string {
  const totals = SERIES.map((s) => ({
    label: s.label,
    total: data.reduce((sum, point) => sum + point[s.key], 0),
  })).filter((t) => t.total > 0);

  if (!totals.length) {
    return `Daily activity trend across ${data.length} day${data.length === 1 ? '' : 's'}: no activity recorded.`;
  }

  return `Daily activity trend across ${data.length} day${data.length === 1 ? '' : 's'}, totals by category: ${totals
    .map((t) => `${t.label} ${t.total.toLocaleString()}`)
    .join(', ')}.`;
}

export function UsageTrendChart({ data, isLoading }: UsageTrendChartProps) {
  if (isLoading) {
    return (
      <Card className="border-slate-200/60 dark:border-slate-700/60 h-full">
        <CardHeader className="pb-2">
          <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-violet-500/10 dark:bg-violet-500/20 flex items-center justify-center">
            <div className="h-4 w-4 rounded-sm bg-violet-500" />
          </div>
          <div>
            <CardTitle className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Activity Trend
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">Daily activity over period</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!data.length ? (
          <div className="h-64 flex items-center justify-center">
            <p className="text-sm text-slate-400 dark:text-slate-500">No trend data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart
              data={data}
              margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
              {...chartA11yProps(buildTrendLabel(data))}
            >
              <defs>
                {SERIES.map((s) => (
                  <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={s.color} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                tickFormatter={formatDate}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }}
                iconType="circle"
                iconSize={6}
              />
              {SERIES.map((s) => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={1.5}
                  fill={`url(#grad-${s.key})`}
                  dot={false}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
